import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomButton from "../components/Button";
import useIsMobile from "../Hooks/useIsMobile";
import apiService from "../Services/api.service";
import CanvasImage from "../components/canvas";
const getContrastTextColor = (bgColor) => {
  const hex = bgColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 125 ? "black" : "white";
};

const getInitials = (name) => {
  if (!name) return "NA";
  const parts = name.split(" ");
  return parts
    .map((p) => p[0])
    .join("")
    .toUpperCase();
};

const TestPage = () => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const auth = localStorage.getItem("token");
  const locationCheck = useLocation();
  const { tid, detections, uid, name, kioskId, doctor } =
    locationCheck.state || {};
  const [popupImage, setPopupImage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [images, setImages] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [visibleDiseases, setVisibleDiseases] = useState({});
  const toggleDisease = (name) => {
    setVisibleDiseases((prev) => ({
      ...prev,
      [tabIndex]: {
        ...(prev[tabIndex] || {}),
        [name]: !(prev[tabIndex] || {})[name],
      },
    }));
  };



  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPopupImage(null);
  };
  useEffect(() => {
    const fetchImages = async () => {
      if (!detections || detections.length === 0) return;

      try {
        const results = await Promise.all(
          detections.map(async (det) => {
            const url = await apiService.getImage(auth, det.diseaseFileURL);

            // Decode image to avoid delay later
            const img = new Image();
            img.src = url;

            await img.decode(); // Wait until fully ready
            return img;
          })
        );

        setImages(results);

        const initialVisible = {};
        detections.forEach((det, idx) => {
          initialVisible[idx] = {};
          det.diseaseDetailedList?.forEach((disease) => {
            initialVisible[idx][disease.name] = true;
          });
        });
        setVisibleDiseases(initialVisible);
      } catch (error) {
        console.error("Error fetching or decoding images:", error);
      }
    };

    fetchImages();
  }, [detections, auth]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  if (!detections || detections.length === 0) {
    return (
      <Box p={2}>
        <Typography>No detections found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", typography: "body1", mt: 2 }}>
      {/* Profile Section */}
      <Box
        display="flex"
        alignItems="center"
        p={2}
        sx={{
          border: "1px solid #eee",
          borderRadius: "10px",
          mb: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Avatar
          sx={{
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.primary.main,
            color: theme.palette.getContrastText(
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.primary.main
            ),
            width: 60,
            height: 60,
            fontSize: 24,
            mr: 2,
          }}
        >
          {getInitials(name)}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {name || "Unknown Name"}
          </Typography>
          <Typography variant="body2" color="textPrimary">
            Associate Doctor: {doctor || "-"}
          </Typography>
          
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Detection Tabs"
        sx={{
          borderBottom: "1px solid #eee",
          ".MuiTab-root": {
            textTransform: "none",
            fontWeight: "500",
          },
          ".Mui-selected": {
            color: "#7c3aed !important",
          },
          ".MuiTabs-indicator": {
            backgroundColor: "#7c3aed",
          },
        }}
      >
        {detections.map((detection, index) => (
          <Tab key={index} label={`Angle: ${detection.angle}`} />
        ))}
      </Tabs>

      
      <Box
        mt={3}
        p={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {images[tabIndex] ? (
          <Box>
            <CanvasImage
              key={tabIndex}
              imageObj={images[tabIndex]}
              maxWidth={600}
              maxHeight={700}
              coordinates={
                detections[tabIndex]?.diseaseDetailedList
                  ?.filter(
                    (item) => visibleDiseases[tabIndex]?.[item.name] !== false
                  )
                  .map((item) => ({
                    coords: item.coordinates.map(([x1, y1, x2, y2]) => [
                      x1,
                      y1,
                      x2,
                      y2,
                    ]),
                    color: item.color,
                  })) || []
              }
            />

            <Box
              mt={2}
              display="flex"
              flexWrap="wrap"
              gap={1}
              justifyContent="center"
            >
              {detections[tabIndex]?.diseaseDetailedList?.map((item) => {
                const bg = item.color;
                const textColor = getContrastTextColor(bg);
                const isActive =
                  visibleDiseases[tabIndex]?.[item.name] !== false;

                return (
                  <CustomButton
                    key={item._id}
                    text={item.name}
                    onClick={() => toggleDisease(item.name)}
                    backgroundColor={bg}
                    color={textColor}
                    hoverColor={bg}
                    width="auto"
                    style={{
                      opacity: isActive ? 1 : 0.5,
                      fontWeight: "bold",
                      paddingInline: "12px",
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ) : (
          <CircularProgress />
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          {popupImage ? (
            <img
              src={popupImage}
              alt="Base"
              style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }}
            />
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TestPage;
